import type { Post } from "@/types";
import type { CcPlatformSdk } from "@cc-consulting-nv/ccsdk";
import { resolveApiBaseUrl } from "@/utils/env";

export interface VideoViewSegment {
  post_ulid: string;
  start_second: number;
  end_second: number;
  last_position: number;
}

class VideoViewTracker {
  private static instance: VideoViewTracker | null = null;

  private pendingViews: VideoViewSegment[] = [];
  private batchInterval = 60000; // Send batches every minute
  private segmentDuration = 5; // 5-second segments
  private batchTimer: ReturnType<typeof setInterval> | null = null;
  private lastTrackedTimes = new Map<string, number>(); // postUlid -> lastTrackedTime
  private isInitialized = false;
  private sdk: CcPlatformSdk | null = null;
  private isAuthenticated = false;

  private constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.beforeUnload();
      });
    }
  }

  public static getInstance(): VideoViewTracker {
    if (!VideoViewTracker.instance) {
      VideoViewTracker.instance = new VideoViewTracker();
    }
    return VideoViewTracker.instance;
  }

  public initialize(sdk: CcPlatformSdk, authenticated = false): void {
    this.sdk = sdk;
    this.isAuthenticated = authenticated;

    if (!this.isInitialized) {
      this.startBatchTimer();
      this.isInitialized = true;
    }
  }

  public setAuthenticated(authenticated: boolean): void {
    this.isAuthenticated = authenticated;
  }

  /**
   * Track a time update from the video player.
   * Called on each `timeupdate` event from the <video> element.
   */
  public trackTimeUpdate(postUlid: string, currentTime: number, duration: number): void {
    if (!postUlid) return;

    const lastTrackedTime = this.lastTrackedTimes.get(postUlid) || 0;
    const currentSecond = Math.floor(currentTime);
    const lastSecond = Math.floor(lastTrackedTime);

    // Track when we cross a new 5-second segment boundary
    const currentSegment = Math.floor(currentSecond / this.segmentDuration);
    const lastSegment = Math.floor(lastSecond / this.segmentDuration);

    if (currentSegment !== lastSegment && currentSecond > lastSecond) {
      const segmentStart = Math.floor(lastSecond / this.segmentDuration) * this.segmentDuration;
      const segmentEnd = Math.min(segmentStart + this.segmentDuration - 1, Math.floor(duration));

      if (segmentEnd >= segmentStart) {
        this.addSegment(postUlid, segmentStart, segmentEnd, currentSecond);
      }
    }

    this.lastTrackedTimes.set(postUlid, currentTime);
  }

  /**
   * Track when the video ends (send the final segment).
   */
  public trackEnded(postUlid: string, duration: number): void {
    if (!postUlid) return;

    const lastTrackedTime = this.lastTrackedTimes.get(postUlid) || 0;
    const lastSecond = Math.floor(lastTrackedTime);
    const endSecond = Math.floor(duration);

    if (endSecond > lastSecond) {
      this.addSegment(postUlid, lastSecond, endSecond, endSecond);
    }

    // Flush immediately on video end
    void this.sendBatch();
  }

  /**
   * Handle seek — reset last tracked time to avoid gap-based false segments.
   */
  public onSeek(postUlid: string, newTime: number): void {
    if (!postUlid) return;
    this.lastTrackedTimes.set(postUlid, newTime);
  }

  /**
   * Reset tracking for a post (e.g., when switching videos).
   */
  public resetPost(postUlid: string): void {
    this.lastTrackedTimes.delete(postUlid);
  }

  public async sendBatch(): Promise<void> {
    if (this.pendingViews.length === 0 || !this.sdk) return;

    const views = [...this.pendingViews];
    this.pendingViews = [];

    try {
      if (this.isAuthenticated) {
        await this.sdk.sendVideoViews({ views });
      } else {
        await this.sdk.sendVideoViewsNoAuth({ views });
      }
    } catch (error) {
      console.error("Failed to send video view batch:", error);
      // Re-add failed views (with cap to prevent infinite growth)
      if (this.pendingViews.length < 100) {
        this.pendingViews.unshift(...views.slice(0, 50));
      }
    }
  }

  public destroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.pendingViews.length > 0) {
      void this.sendBatch();
    }

    this.pendingViews = [];
    this.lastTrackedTimes.clear();
    this.isInitialized = false;
    VideoViewTracker.instance = null;
  }

  public getPendingViewsCount(): number {
    return this.pendingViews.length;
  }

  public getTrackedPosts(): string[] {
    return Array.from(this.lastTrackedTimes.keys());
  }

  private addSegment(postUlid: string, startSecond: number, endSecond: number, currentPosition: number): void {
    const segmentStart = Math.floor(startSecond / this.segmentDuration) * this.segmentDuration;
    const segmentEnd = Math.min(segmentStart + this.segmentDuration - 1, endSecond);

    // Avoid duplicates
    const isDuplicate = this.pendingViews.some(
      (v) => v.post_ulid === postUlid && v.start_second === segmentStart && v.end_second === segmentEnd,
    );
    if (isDuplicate) return;

    this.pendingViews.push({
      post_ulid: postUlid,
      start_second: segmentStart,
      end_second: segmentEnd,
      last_position: Math.floor(currentPosition),
    });

    if (this.pendingViews.length >= 20) {
      void this.sendBatch();
    }
  }

  private startBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    this.batchTimer = setInterval(() => {
      void this.sendBatch();
    }, this.batchInterval);
  }

  private beforeUnload(): void {
    if (this.pendingViews.length === 0) return;

    const payload = { views: [...this.pendingViews] };

    // Try SDK async (may not complete)
    if (this.sdk) {
      (this.isAuthenticated
        ? this.sdk.sendVideoViews(payload)
        : this.sdk.sendVideoViewsNoAuth(payload)
      ).catch(() => {});
    }

    // sendBeacon as fallback for guaranteed delivery
    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const endpoint = this.isAuthenticated ? "/v1/video-views" : "/v1/video-views-na";
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(`${apiBaseUrl}${endpoint}`, blob);
    } catch {
      // Silent fail on unload
    }
  }
}

export const videoViewTracker = VideoViewTracker.getInstance();
export default videoViewTracker;

if (typeof window !== "undefined") {
  Object.defineProperty(window, "videoViewTracker", {
    get: () => videoViewTracker,
    configurable: true,
  });
}
